/**
 * DYNAMIC CONDITION PAGE
 * Template for all condition pages across all sites
 * Loads content based on conditionId and current site
 */

import React from 'react';
import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { useSite } from '../contexts/SiteContext';
import { 
  ArrowLeft,
  ArrowRight,
  CheckCircle2,
  Phone,
  AlertTriangle,
  Activity,
  Clock,
  Stethoscope,
  Clipboard
} from 'lucide-react';

export const ConditionPage = ({ conditionId }) => {
  const { 
    conditions, 
    theme, 
    doctor, 
    bodyPart,
    specialtyLabel 
  } = useSite();

  // Find the current condition
  const condition = conditions.find(c => c.id === conditionId);
  
  // Get related conditions (next and previous in list)
  const currentIndex = conditions.findIndex(c => c.id === conditionId);
  const relatedConditions = conditions.filter((c, i) => 
    i !== currentIndex && Math.abs(i - currentIndex) <= 2
  ).slice(0, 3);

  if (!condition) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-2xl font-bold text-gray-900 mb-4">Condition Not Found</h1>
          <Link to="/">
            <Button>Return Home</Button>
          </Link>
        </div>
      </div>
    );
  }

  // Generic content structure for conditions
  // In a real app, this would come from a database/CMS
  const conditionContent = {
    overview: `${condition.title} is a common ${bodyPart.toLowerCase()} condition that affects many patients. Understanding the causes, symptoms, and treatment options is crucial for proper management and recovery.`,
    
    causes: [
      'Age-related wear and tear',
      'Repetitive strain or overuse',
      'Acute injury or trauma',
      'Poor posture or biomechanics',
      'Underlying medical conditions'
    ],
    
    symptoms: [
      `Pain in the ${bodyPart.toLowerCase()} region`,
      'Stiffness or limited range of motion',
      'Weakness or instability',
      'Swelling or inflammation',
      'Clicking or grinding sensations'
    ],
    
    diagnosis: [
      'Physical examination',
      'X-ray imaging',
      'MRI scan',
      'Ultrasound',
      'CT scan (if needed)'
    ],
    
    treatments: {
      conservative: [
        'Rest and activity modification',
        'Physical therapy',
        'Anti-inflammatory medications',
        'Ice and heat therapy',
        'Bracing or support'
      ],
      surgical: [
        'Minimally invasive arthroscopy',
        'Open surgical repair',
        'Reconstruction procedures',
        'Joint replacement (in severe cases)'
      ]
    },
    
    recovery: {
      timeline: '4-12 weeks for conservative treatment, 3-6 months for surgical intervention',
      physio: 'Physical therapy is essential for optimal recovery',
      followUp: 'Regular follow-up appointments to monitor progress'
    }
  };

  return (
    <div className="min-h-screen" data-testid={`condition-page-${conditionId}`}>
      {/* Hero Section */}
      <section 
        className="py-16 md:py-24"
        style={{ backgroundColor: theme.lightBgHex }}
      >
        <div className="container mx-auto px-4">
          {/* Breadcrumb */}
          <div className="flex items-center gap-2 text-sm text-gray-600 mb-6">
            <Link to="/" className="hover:text-gray-900">Home</Link>
            <span>/</span>
            <Link to="/" className="hover:text-gray-900">{bodyPart} Conditions</Link>
            <span>/</span>
            <span style={{ color: theme.primaryHex }}>{condition.title}</span>
          </div>

          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <h1 className="text-3xl md:text-4xl lg:text-5xl font-bold text-gray-900 mb-6">
                {condition.title}
              </h1>
              <p className="text-lg text-gray-600 mb-8">
                {condition.description}
              </p>
              <div className="flex flex-col sm:flex-row gap-4">
                <a href={doctor.consultationLink} target="_blank" rel="noopener noreferrer">
                  <Button 
                    size="lg" 
                    style={{ backgroundColor: theme.primaryHex }}
                    className="text-white hover:opacity-90 w-full sm:w-auto"
                  >
                    <Phone className="w-5 h-5 mr-2" />
                    Consult {specialtyLabel}
                  </Button>
                </a>
                <Link to="/contact">
                  <Button 
                    size="lg" 
                    variant="outline"
                    className="w-full sm:w-auto"
                    style={{ borderColor: theme.primaryHex, color: theme.primaryHex }}
                  >
                    Ask a Question
                  </Button>
                </Link>
              </div>
            </div>

            {/* Condition Image */}
            <div className="relative">
              <div 
                className="aspect-video rounded-2xl overflow-hidden shadow-xl"
                style={{ boxShadow: `0 20px 40px -12px ${theme.primaryHex}30` }}
              >
                <img
                  src="https://images.unsplash.com/photo-1559757175-5700dde675bc?w=800"
                  alt={condition.title}
                  className="w-full h-full object-cover"
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Overview */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl">
            <h2 className="text-2xl md:text-3xl font-bold text-gray-900 mb-6">
              Overview
            </h2>
            <p className="text-lg text-gray-600 leading-relaxed">
              {conditionContent.overview}
            </p>
          </div>
        </div>
      </section>

      {/* Causes & Symptoms */}
      <section className="py-16" style={{ backgroundColor: theme.lightBgHex }}>
        <div className="container mx-auto px-4">
          <div className="grid md:grid-cols-2 gap-12">
            {/* Causes */}
            <div>
              <div className="flex items-center gap-3 mb-6">
                <div 
                  className="w-12 h-12 rounded-lg flex items-center justify-center"
                  style={{ backgroundColor: `${theme.primaryHex}20` }}
                >
                  <AlertTriangle className="w-6 h-6" style={{ color: theme.primaryHex }} />
                </div>
                <h2 className="text-2xl font-bold text-gray-900">Common Causes</h2>
              </div>
              <ul className="space-y-4">
                {conditionContent.causes.map((cause, index) => (
                  <li key={index} className="flex items-start gap-3">
                    <CheckCircle2 
                      className="w-5 h-5 flex-shrink-0 mt-1" 
                      style={{ color: theme.primaryHex }}
                    />
                    <span className="text-gray-700">{cause}</span>
                  </li>
                ))}
              </ul>
            </div>

            {/* Symptoms */}
            <div>
              <div className="flex items-center gap-3 mb-6">
                <div 
                  className="w-12 h-12 rounded-lg flex items-center justify-center"
                  style={{ backgroundColor: `${theme.primaryHex}20` }}
                >
                  <Activity className="w-6 h-6" style={{ color: theme.primaryHex }} />
                </div>
                <h2 className="text-2xl font-bold text-gray-900">Signs & Symptoms</h2>
              </div>
              <ul className="space-y-4">
                {conditionContent.symptoms.map((symptom, index) => (
                  <li key={index} className="flex items-start gap-3">
                    <CheckCircle2 
                      className="w-5 h-5 flex-shrink-0 mt-1" 
                      style={{ color: theme.primaryHex }}
                    />
                    <span className="text-gray-700">{symptom}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* Diagnosis */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-4">
          <div className="flex items-center gap-3 mb-8">
            <div 
              className="w-12 h-12 rounded-lg flex items-center justify-center"
              style={{ backgroundColor: `${theme.primaryHex}20` }}
            >
              <Clipboard className="w-6 h-6" style={{ color: theme.primaryHex }} />
            </div>
            <h2 className="text-2xl md:text-3xl font-bold text-gray-900">Diagnosis</h2>
          </div>
          
          <div className="grid md:grid-cols-5 gap-4">
            {conditionContent.diagnosis.map((item, index) => (
              <Card key={index} className="text-center">
                <CardContent className="p-6">
                  <div 
                    className="w-10 h-10 rounded-full flex items-center justify-center mx-auto mb-3 text-white font-bold"
                    style={{ backgroundColor: theme.primaryHex }}
                  >
                    {index + 1}
                  </div>
                  <p className="text-gray-700 font-medium">{item}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Treatment Options */}
      <section className="py-16" style={{ backgroundColor: theme.lightBgHex }}>
        <div className="container mx-auto px-4">
          <div className="flex items-center gap-3 mb-8">
            <div 
              className="w-12 h-12 rounded-lg flex items-center justify-center"
              style={{ backgroundColor: `${theme.primaryHex}20` }}
            >
              <Stethoscope className="w-6 h-6" style={{ color: theme.primaryHex }} />
            </div>
            <h2 className="text-2xl md:text-3xl font-bold text-gray-900">Treatment Options</h2>
          </div>

          <div className="grid md:grid-cols-2 gap-8">
            {/* Conservative */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <span 
                    className="w-3 h-3 rounded-full"
                    style={{ backgroundColor: '#22c55e' }}
                  ></span>
                  Conservative (Non-Surgical)
                </CardTitle>
              </CardHeader>
              <CardContent>
                <ul className="space-y-3">
                  {conditionContent.treatments.conservative.map((treatment, index) => (
                    <li key={index} className="flex items-start gap-3">
                      <CheckCircle2 className="w-5 h-5 text-green-500 flex-shrink-0 mt-0.5" />
                      <span className="text-gray-700">{treatment}</span>
                    </li>
                  ))}
                </ul>
              </CardContent>
            </Card>

            {/* Surgical */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <span 
                    className="w-3 h-3 rounded-full"
                    style={{ backgroundColor: theme.primaryHex }}
                  ></span>
                  Surgical Options
                </CardTitle>
              </CardHeader>
              <CardContent>
                <ul className="space-y-3">
                  {conditionContent.treatments.surgical.map((treatment, index) => (
                    <li key={index} className="flex items-start gap-3">
                      <CheckCircle2 
                        className="w-5 h-5 flex-shrink-0 mt-0.5" 
                        style={{ color: theme.primaryHex }}
                      />
                      <span className="text-gray-700">{treatment}</span>
                    </li>
                  ))}
                </ul>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Recovery */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-4">
          <div className="flex items-center gap-3 mb-8">
            <div 
              className="w-12 h-12 rounded-lg flex items-center justify-center"
              style={{ backgroundColor: `${theme.primaryHex}20` }}
            >
              <Clock className="w-6 h-6" style={{ color: theme.primaryHex }} />
            </div>
            <h2 className="text-2xl md:text-3xl font-bold text-gray-900">Recovery & Prognosis</h2>
          </div>

          <div className="grid md:grid-cols-3 gap-6">
            <Card className="bg-gray-50">
              <CardHeader>
                <CardTitle className="text-lg">Recovery Timeline</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-gray-600">{conditionContent.recovery.timeline}</p>
              </CardContent>
            </Card>
            <Card className="bg-gray-50">
              <CardHeader>
                <CardTitle className="text-lg">Physical Therapy</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-gray-600">{conditionContent.recovery.physio}</p>
              </CardContent>
            </Card>
            <Card className="bg-gray-50">
              <CardHeader>
                <CardTitle className="text-lg">Follow-Up Care</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-gray-600">{conditionContent.recovery.followUp}</p>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Related Conditions */}
      {relatedConditions.length > 0 && (
        <section className="py-16" style={{ backgroundColor: theme.lightBgHex }}>
          <div className="container mx-auto px-4">
            <h2 className="text-2xl md:text-3xl font-bold text-gray-900 mb-8">
              Related {bodyPart} Conditions
            </h2>
            <div className="grid md:grid-cols-3 gap-6">
              {relatedConditions.map((relCondition) => (
                <Link key={relCondition.id} to={relCondition.slug}>
                  <Card className="h-full hover:shadow-lg transition-shadow cursor-pointer">
                    <CardHeader>
                      <CardTitle className="text-lg">{relCondition.title}</CardTitle>
                    </CardHeader>
                    <CardContent>
                      <p className="text-gray-600 text-sm mb-4">{relCondition.description}</p>
                      <span 
                        className="text-sm font-medium inline-flex items-center gap-1"
                        style={{ color: theme.primaryHex }}
                      >
                        Learn more <ArrowRight className="w-4 h-4" />
                      </span>
                    </CardContent>
                  </Card>
                </Link>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* CTA */}
      <section 
        className="py-16"
        style={{ backgroundColor: theme.primaryHex }}
      >
        <div className="container mx-auto px-4 text-center">
          <h2 className="text-2xl md:text-3xl font-bold text-white mb-4">
            Need Help With {condition.title}?
          </h2>
          <p className="text-white/80 mb-8 max-w-2xl mx-auto">
            Get expert advice from {doctor.name}. Book a consultation to discuss your condition and treatment options.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <a href={doctor.consultationLink} target="_blank" rel="noopener noreferrer">
              <Button size="lg" className="bg-white hover:bg-gray-100" style={{ color: theme.primaryHex }}>
                <Phone className="w-5 h-5 mr-2" />
                Book Consultation
              </Button>
            </a>
            <Link to="/">
              <Button size="lg" variant="outline" className="border-2 border-white text-white hover:bg-white/10">
                <ArrowLeft className="w-5 h-5 mr-2" />
                Back to Home
              </Button>
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};
